import {Typography} from '@material-ui/core'
import React, {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import http from '../http'
import {makeOrder, OrderContext} from '../OrderContext'
import {ReturnsListContext} from '../ReturnsList/ReturnsListContext'
import {PreviousButton, ReturnToOrders} from '../StepperButtons'
import PrintButton from './PrintButton'
import {format} from "date-fns";

type OrderNumber = { number: string, type: 'ZIS2' | 'Exchange' | 'Return' }

type OrderCreateResponse = {
    orderNumbers?: OrderNumber[],
    orderSummary?: string
}

type OrderSuccessProps = { orderNumbers?: OrderNumber[] }

const OrderSuccess: React.FC<OrderSuccessProps> = ({orderNumbers}) => {
    const {pickup, orderConfirmationTranslationKey} = useContext(OrderContext)
    const {t} = useTranslation()

    if (!orderNumbers) return null

    return (
        <>
            <Typography variant='h5'>{t(orderConfirmationTranslationKey)}</Typography>
            <p>{t('order_numbers')}:</p>

            <p><b>{t('return')}:</b> {orderNumbers.find(o => o.type === 'Exchange' || o.type === 'Return')?.number}</p>
            {orderNumbers.some(o => o.type === 'ZIS2') &&
                <p><b>{t('exchange')}:</b> {orderNumbers.find(o => o.type === 'ZIS2')?.number}</p>}

            <br/>

            <Typography variant='h5'>{t('next_steps')}</Typography>
            {pickup === 'NoPickup'
                ? <>
                    <p>{t('ship_return')}</p>
                </>
                : <>
                    <p>{t('email_confirmation')}</p>
                    <p>{t('start_pickup')}</p>
                    <br/>
                </>
            }

            <div style={{display: 'flex', gap: 15}}>
                <PrintButton
                    returnOrderNumber={orderNumbers.find(o => o.type === 'Exchange' || o.type === 'Return')?.number}
                    exchangeOrderNumber={orderNumbers.find(o => o.type === 'ZIS2')?.number}
                />
                <ReturnToOrders/>
            </div>
        </>
    )
}

const OrderFailed = () => {
    const {t} = useTranslation()

    return (
        <>
            <Typography variant='h5'>{t('order_create_issue')}</Typography>
            <p>{t('try_again_later')}</p>
            <br/>
            <PreviousButton/>
        </>
    )
}

export const getStorageValue = (key: string) => JSON.parse(sessionStorage.getItem('orderCreate') || '{}')[key]

const OrderConfirmation = () => {
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<boolean>(false)
    const [create, setCreate] = useState<OrderNumber[]>()
    const {
        order,
        pickup,
        shipTo,
        orderType,
        billTo,
        session,
        simulate,
        purchases,
        customer,
        pickupDate
    } = useContext(OrderContext)
    const {reload} = useContext(ReturnsListContext)
    const o = useMemo(() => ({
        ...makeOrder(order, orderType, purchases),
        shipTo,
        pickup,
        billTo,
        customer: {
            shippingMethod: customer.shippingMethod,
            division: customer.division,
            distributionChannel: customer.distributionChannel,
            paymentTerm: customer.paymentTerm
        },
        pickupDate: pickup !== 'NoPickup' ? format(pickupDate, 'yyyy-MM-dd') : undefined,
    }), [shipTo, pickup, order, orderType, billTo, purchases, customer])
    const {t} = useTranslation()

    // Guards against duplicate POST /api/OrderCreate for the same session.
    // The sessionStorage guard below is only written after the response arrives,
    // so an effect re-run (e.g. simulate state changing, or any dependency
    // identity change) while the request is in flight would otherwise fire a
    // second POST and create a duplicate order in SAP.
    const submittedSessionRef = useRef<string | null>(null)
    // simulate is stored in sessionStorage inside .then; keep it in a ref so
    // it does not need to be a useEffect dependency (which would re-run this
    // effect and trigger duplicate submissions when the debounced simulator
    // finishes during the step transition).
    const simulateRef = useRef(simulate)
    useEffect(() => { simulateRef.current = simulate }, [simulate])

    useEffect(() => {
        if (submittedSessionRef.current === session) return
        if (getStorageValue('session') === session) {
            submittedSessionRef.current = session
            setCreate(getStorageValue('orders'))
            return
        }
        submittedSessionRef.current = session
        setLoading(true)
        setError(false)

        http.post<OrderCreateResponse>('/api/OrderCreate', o)
            .then(({data}) => {
                if (!data.orderNumbers) {
                    // Allow retry (e.g. via PreviousButton) if the server did
                    // not actually create an order.
                    submittedSessionRef.current = null
                    return setError(true)
                }
                setCreate(data.orderNumbers)
                sessionStorage.setItem('orderCreate', JSON.stringify({session, orders: data.orderNumbers, simulate: simulateRef.current}))
                reload();
            })
            .catch(() => {
                submittedSessionRef.current = null
                setError(true)
            })
            .finally(() => {
                setLoading(false);
            })
    }, [o, reload, session])

    return (
        <>
            {loading && <p>{t('submitting_order')}...</p>}
            {!loading && !error && <OrderSuccess orderNumbers={create}/>}
            {!loading && error && <OrderFailed/>}
        </>
    )
}

export default OrderConfirmation
